<template>
  <v-dialog :model-value="!!reminder" max-width="600" persistent scrollable @keydown.esc="dismiss">
    <v-card v-if="reminder">
      <v-card-title class="text-wrap">DSN à néant — {{ reminder.mois }}</v-card-title>
      <v-card-text>
        <p><strong>{{ reminder.nom }}</strong> — SIRET {{ reminder.siret }}</p>
        <p>Selon nos informations, vous n'avez pas déclaré d'artiste ou de technicien au mois de {{ reminder.mois }}.</p>
        <p>Une déclaration d'activité à néant doit être adressée aux organismes sociaux. Votre offre PRELUDE ne comprend pas l'envoi des DSN à néant.</p>
        <p v-if="reminder.avantDateLimite">
          Pour nous confier cette déclaration, souscrivez à l'offre PIANO depuis votre espace abonnement,
          au plus tard le <strong>{{ reminder.dateLimite }}</strong>.
        </p>
        <p>Si vous effectuez cette déclaration vous-même, vous pouvez ignorer ce rappel.</p>
        <p>Le défaut de déclaration vous expose à une taxation d'office de la part de l'URSSAF et de la DGFIP ainsi qu'à des pénalités de retard.</p>
        <p>Pour toute question, contactez vos conseillers via le chat ou à <a href="mailto:info@cheque-intermittents.com">info@cheque-intermittents.com</a>.</p>
      </v-card-text>
      <v-card-actions class="justify-end pa-4">
        <loader-button v-if="reminder.avantDateLimite" label="Voir les abonnements" @callback="showSubscriptions" />
        <loader-button label="J'ai compris" @callback="dismiss" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { storeSession } from '@/generated/stores';
import Dsn, { DsnReminder } from '@/generated/classes/Dsn';
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import Route from '@/generated/classes/Route';
import { Fiches, Modules } from '@/generated/classes/AppSchema';
import { goToModule } from '@/framework/base/helpers/NavHelper';

export default defineComponent({
  components: { LoaderButton },
  data() {
    return {
      reminders: [] as DsnReminder[],
      dismissed: {} as Record<string, string>,
      requestId: 0,
    };
  },
  computed: {
    userId(): number {
      return storeSession.sessionValid ? storeSession.connectedUser?.id ?? 0 : 0;
    },
    reminder(): DsnReminder | undefined {
      return this.reminders[0];
    },
  },
  watch: {
    userId: { immediate: true, handler: 'loadReminders' },
    '$route.fullPath': 'loadReminders',
  },
  mounted() {
    window.addEventListener('focus', this.loadReminders);
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.loadReminders);
    this.requestId++;
  },
  methods: {
    storageKey(reminder: DsnReminder): string {
      return `dsn-neant:${this.userId}:${reminder.etablissementId}`;
    },
    async loadReminders(): Promise<void> {
      const requestId = ++this.requestId;
      this.reminders = [];
      if (!this.userId || !storeSession.httpCx) return;
      try {
        const reminders = await Dsn.reminders();
        if (requestId !== this.requestId || !Array.isArray(reminders)) return;
        this.reminders = reminders.filter(reminder => {
          const key = this.storageKey(reminder);
          if (this.dismissed[key] === reminder.periode) return false;
          try {
            return localStorage.getItem(key) !== reminder.periode;
          } catch { /* Keep the dismissal in memory when browser storage is unavailable. */ }
          return true;
        });
      } catch {
        // A reminder must not prevent access to the user's account.
      }
    },
    dismiss(): void {
      if (!this.reminder) return;
      const key = this.storageKey(this.reminder);
      this.dismissed[key] = this.reminder.periode;
      try {
        localStorage.setItem(key, this.reminder.periode);
      } catch { /* The in-memory dismissal still applies for this visit. */ }
      this.reminders.shift();
    },
    showSubscriptions(): void {
      this.dismiss();
      const route = new Route();
      route.module = Modules.ABONNEMENT;
      route.fiche = Fiches.ABONNEMENT_LIST;
      goToModule(this.$router, route);
    },
  },
});
</script>
